import { useTheme } from "../hooks/use-theme";
import React, { useCallback, useMemo, useState } from "react";
import { LayoutChangeEvent, StyleProp, StyleSheet, TextStyle, View, ViewStyle } from "react-native";
import Text from "../components/Text";

const ALIGN_MAP: Record<"start" | "center" | "end", "left" | "center" | "right" | "justify"> = {
    start: "left",
    center: "center",
    end: "right",
};

type TableCellType = "header" | "body" | "footer" | "hr";

export type TableDataCell = {
    value: React.ReactNode | string;
    bold?: boolean;
    highlight?: boolean;
    lineBottom?: boolean;
    textStyle?: StyleProp<TextStyle>;
    textColor?: string;
    style?: StyleProp<ViewStyle>;
    colspan?: number;
    textAlign?: "start" | "center" | "end";
};

export type TableDataRow = {
    type?: "header" | "body" | "footer";
    cells: TableDataCell[];
    hide?: boolean;
};

type TDProps = {
    cell: TableDataCell;
    cellWidth: number;
    cellIndex: number;
    nbColumns: number;
    type?: TableCellType;
};

const TD: React.FC<TDProps> = ({ cell, cellWidth, type, cellIndex, nbColumns }) => {
    const styles = useStyleSheet();
    const [viewStyle, textStyle] = useMemo(() => {
        switch (type) {
            case "header":
                return [styles.header, styles.headerText];
            case "footer":
                return [styles.footer, styles.footerText];
            case "hr":
                return [styles.hr, null];
            default:
                return [styles.body, styles.bodyText];
        }
    }, [styles, type]);
    const isLast = useMemo(() => {
        return cellIndex === nbColumns - 1;
    }, [cellIndex, nbColumns]);

    return (
        <View
            style={[
                styles.td,
                viewStyle,
                cell.lineBottom ? styles.lineBottom : undefined,
                cell.style,
                { width: cellWidth * (cell.colspan || 1) },
            ]}
        >
            {typeof cell.value === "string" ? (
                <Text
                    style={[
                        styles.tdText,
                        isLast ? { textAlign: ALIGN_MAP["end"] } : undefined,
                        cell.textAlign ? { textAlign: ALIGN_MAP[cell.textAlign] } : undefined,
                        cell.textStyle,
                        textStyle,
                        cell.bold ? styles.bold : styles.normal,
                        cell.highlight ? styles.highlight : undefined,
                        cell.textColor ? { color: cell.textColor } : undefined,
                    ]}
                >
                    {cell.value}
                </Text>
            ) : (
                cell.value
            )}
        </View>
    );
};

type TableRowProps = {
    cells?: TableDataCell[];
    type?: TableCellType;
    cellWidth: number;
    nbColumns: number;
};

const TableRow: React.FC<TableRowProps> = ({ type, cells = [], cellWidth, nbColumns }) => {
    const styles = useStyleSheet();

    return (
        <View style={[styles.tr, type === "hr" ? styles.hr : null]}>
            {cells.map((cell, index) => {
                if (!cell) {
                    return null;
                }
                return (
                    <TD
                        key={index}
                        cell={cell}
                        cellWidth={cellWidth}
                        type={type}
                        cellIndex={index}
                        nbColumns={nbColumns}
                    />
                );
            })}
        </View>
    );
};
type TableProps = {
    data: TableDataRow[];
};

const Table: React.FC<TableProps> = ({ data }) => {
    const styles = useStyleSheet();
    const [width, setWidth] = useState(0);

    const onLayout = useCallback((e: LayoutChangeEvent) => {
        setWidth(e.nativeEvent.layout.width);
    }, []);

    const nbColumns = useMemo(() => {
        return data.reduce((acc, row) => {
            return Math.max(acc, row.cells?.length || 0);
        }, 1);
    }, [data]);

    const cellWidth = useMemo(() => {
        return width / nbColumns;
    }, [width, nbColumns]);

    return (
        <View style={styles.table} onLayout={onLayout}>
            {data.map((row, index) =>
                !row.hide ? (
                    <TableRow
                        key={index}
                        type={row.type}
                        cells={row.cells}
                        cellWidth={cellWidth}
                        nbColumns={nbColumns}
                    />
                ) : null,
            )}
        </View>
    );
};

const useStyleSheet = () => {
    const { colors, fonts, gridSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                table: {
                    flex: 1,
                    flexDirection: "column",
                },
                hr: {
                    borderBottomWidth: 1,
                    borderColor: colors.accentLighter,
                },
                td: {},
                tr: {
                    flexDirection: "row",
                    flex: 1,
                    width: "100%",
                },
                tdText: {},
                bold: {
                    ...fonts.bodyLarge,
                },
                normal: {},
                highlight: {
                    color: colors.secondary,
                },
                lineBottom: {},
                header: {
                    paddingTop: gridSize,
                    paddingBottom: gridSize / 4,
                },
                headerText: {
                    ...fonts.bodyLarge,
                },
                body: {
                    paddingVertical: 5,
                },
                bodyText: {
                    ...fonts.bodyMedium,
                },
                footer: {
                    paddingVertical: gridSize / 2,
                    borderColor: colors.accentLighter,
                    borderBottomWidth: 1,
                },
                footerText: {
                    ...fonts.bodyMedium,
                },
            }),
        [fonts, colors, gridSize],
    );
};

export default Table;
